import { useState, useEffect, useCallback } from "react";
import { useDiet } from "@/contexts/DietContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lock, Plus, FileText, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api";
import MetricsSummaryCards from "@/components/metrics/MetricsSummaryCards";
import MetricsChart from "@/components/metrics/MetricsChart";
import MetricsForm from "@/components/metrics/MetricsForm";
import MetricsHistory from "@/components/metrics/MetricsHistory";
import ProgressPhotos from "@/components/metrics/ProgressPhotos";
import MetricsSkeleton from "@/components/metrics/MetricsSkeleton";
import type { MetricEntry } from "@/components/metrics/MetricsSummaryCards";

const MEASUREMENT_PDF_FILE = "guia_de_avaliacao_e_medidas.pdf";

const Metrics = () => {
  const { selectedPlan, measures } = useDiet();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [entries, setEntries] = useState<MetricEntry[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photosKey, setPhotosKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadEntries = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await apiFetch("api-metrics", { action: "list" });
      const data = (res.data || []).map((d: any) => ({
        id: d.id,
        date: d.date,
        weight: Number(d.weight),
        arm: d.arm ? Number(d.arm) : undefined,
        waist: d.waist ? Number(d.waist) : undefined,
        abdomen: d.abdomen ? Number(d.abdomen) : undefined,
        hip: d.hip ? Number(d.hip) : undefined,
        thigh: d.thigh ? Number(d.thigh) : undefined,
        calf: d.calf ? Number(d.calf) : undefined,
        note: d.note || undefined,
        photoIds: d.photo_ids || [],
      }));
      setEntries(data);
    } catch (e: any) {
      console.error("Failed to load metrics:", e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user && (selectedPlan === "plus" || selectedPlan === "complete")) loadEntries();
    else setLoading(false);
  }, [user, selectedPlan, loadEntries]);

  if (!selectedPlan || selectedPlan === "basic" || selectedPlan === "free") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Medidas Corporais</h2>
        <p className="text-muted-foreground mb-6 max-w-sm">
          O módulo de métricas corporais e dashboard interativo está disponível no <strong>Plano PRO FIT MAX</strong>. Acompanhe sua evolução com gráficos detalhados.
        </p>
        <Button onClick={() => navigate("/plans")} className="gradient-primary text-primary-foreground">
          Ver Planos
        </Button>
      </motion.div>
    );
  }

  const handleAdd = async (entry: MetricEntry, photos: { type: "front" | "side" | "back"; file: File }[]) => {
    setUploading(true);

    // Upload photos first
    const uploadedPhotoIds: string[] = [];
    if (photos.length > 0) {
      const { compressImage } = await import("@/lib/imageCompressor");
      for (const photo of photos) {
        try {
          const compressed = await compressImage(photo.file);
          const formData = new FormData();
          formData.append("file", compressed);
          formData.append("type", photo.type);
          formData.append("date", entry.date);

          const res = await apiFetch("api-photos", { action: "upload" }, {
            method: "POST",
            formData,
          });
          if (res.data?.id) uploadedPhotoIds.push(res.data.id);
        } catch (e: any) {
          toast({ title: `Erro ao enviar foto (${photo.type})`, description: e.message, variant: "destructive" });
        }
      }
      if (uploadedPhotoIds.length > 0) {
        toast({ title: `${uploadedPhotoIds.length} foto(s) salva(s) com sucesso!` });
        setPhotosKey(prev => prev + 1);
      }
    }

    // Save metric entry to backend
    try {
      await apiFetch("api-metrics", { action: "save" }, {
        method: "POST",
        body: {
          date: entry.date,
          weight: entry.weight,
          arm: entry.arm,
          waist: entry.waist,
          abdomen: entry.abdomen,
          hip: entry.hip,
          thigh: entry.thigh,
          calf: entry.calf,
          note: entry.note,
          photo_ids: uploadedPhotoIds,
        },
      });
      await loadEntries();
      toast({ title: "Registro salvo com sucesso!" });
    } catch (e: any) {
      toast({ title: "Erro ao salvar registro", description: e.message, variant: "destructive" });
    }

    setUploading(false);
    setShowForm(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch("api-metrics", { action: "delete", id }, { method: "DELETE" });
      setEntries(prev => prev.filter(e => e.id !== id));
      toast({ title: "Registro excluído com sucesso!" });
    } catch (e: any) {
      toast({ title: "Erro ao excluir registro", description: e.message, variant: "destructive" });
    }
  };

  const latestEntry = entries[entries.length - 1];
  const firstEntry = entries[0];
  const weightDiff = latestEntry && firstEntry ? (latestEntry.weight - firstEntry.weight).toFixed(1) : null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex flex-col items-center text-center lg:flex-row lg:text-left lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Medidas Corporais</h1>
          <p className="text-muted-foreground">Acompanhe sua evolução</p>
        </div>
        <div className="flex flex-col gap-2 w-full lg:flex-row lg:w-auto">
          <Button
            onClick={async () => {
              setDownloadingPdf(true);
              try {
                const { supabase } = await import("@/integrations/supabase/client");
                const { data: sessionData } = await supabase.auth.getSession();
                const token = sessionData?.session?.access_token;
                if (!token) throw new Error("Sessão expirada");
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                const res = await fetch(
                  `${supabaseUrl}/functions/v1/api-materials?file=${encodeURIComponent(MEASUREMENT_PDF_FILE)}`,
                  { headers: { Authorization: `Bearer ${token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
                );
                if (!res.ok) throw new Error("Erro ao baixar");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = MEASUREMENT_PDF_FILE;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
              } catch { toast({ title: "Erro ao baixar o material", variant: "destructive" }); }
              finally { setDownloadingPdf(false); }
            }}
            variant="outline"
            size="sm"
            disabled={downloadingPdf}
            title="Material orientativo de medidas corporais"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Material Orientativo de Medidas Corporais
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="gradient-primary text-primary-foreground" size="sm" translate="no" disabled={loading || uploading}>
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Registro
          </Button>
        </div>
      </div>

      {loading ? (
        <MetricsSkeleton />
      ) : (
        <>
          {latestEntry && (
            <MetricsSummaryCards latestEntry={latestEntry} weightDiff={weightDiff} totalEntries={entries.length} />
          )}

          <MetricsChart entries={entries} />

          {showForm && (
            <MetricsForm
              initialWeight={measures?.weight?.toString()}
              onAdd={handleAdd}
              onCancel={() => setShowForm(false)}
              uploading={uploading}
            />
          )}

          {!showForm && <MetricsHistory entries={entries} onDelete={handleDelete} />}

          <ProgressPhotos key={photosKey} />
        </>
      )}
    </motion.div>
  );
};

export default Metrics;
